import {
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerF,
  useJsApiLoader
} from '@react-google-maps/api';
import { Fab } from '@rmwc/fab';
import { Icon } from '@rmwc/icon';
import { SERVICES_TYPES } from 'Achat/Constants';
import { API_DEV_KEY, API_KEY, ICON_ACTIVE, ICON_INACTIVE, ICON_PAIEMENT_SUR_PLACE } from 'Map/Constants';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { selectIcon } from 'Shared/Utils/Icon';
import "./Map.css";

// #REGION CONSTANTES
const containerStyle = {
  position: "relative",
  width: '100%',
  height: "calc(100% - 72px)",
}

const LEGEND_ICON_SIZE = 20;
const LEGEND_ICON_SCALE = 1.0; 

function MapComponent(props) {
  // #region INITIALISATION
  const {
    center,
    defaultCenter,
    isMarkerShown,
    markers,
    onClick,
    terminauxSelector,
    zoom
  } = props;

  // Initialisation du state
  const [mapCenter, setMapCenter] = useState({ ...center });
  const [activeMarker, setActiveMarker] = useState(null);
  const [map, setMap] = useState(null);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  const [showServiceFilters, setShowServiceFilters] = useState(false);

  // Initialisation de l'API Google Maps
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NODE_ENV === 'production' ? API_KEY : API_DEV_KEY,
    language: "fr",
  });

  // Traduction i18n
  const { t } = useTranslation();
  // #endregion

  // #region SERVICE FILTER
  // Groupes de services affichés au filtre (ex: "elec" regroupe 16A et 32A)
  const SERVICE_TYPE_GROUPS = [
    {
      key: "EAU",
      label: t("map.filters.eau", { defaultValue: "Eau" }),
      iconType: SERVICES_TYPES.EAU_VIDANGE,
      serviceTypes: [SERVICES_TYPES.EAU_VIDANGE, SERVICES_TYPES.EAU_VIDANGE2].filter(Boolean)
    },
    {
      key: "ELEC",
      label: t("map.filters.electricite", { defaultValue: "Electricite" }),
      iconType: SERVICES_TYPES.ELECTRICICTE,
      serviceTypes: [
        SERVICES_TYPES.ELECTRICICTE,
        SERVICES_TYPES.A16,
        SERVICES_TYPES.A32,
        SERVICES_TYPES.A63
      ].filter(Boolean)
    },
    {
      key: "PARKING",
      label: t("map.filters.parking", { defaultValue: "Parking" }),
      iconType: SERVICES_TYPES.PARKING,
      serviceTypes: [SERVICES_TYPES.ACCES_TEMPORAIRE, SERVICES_TYPES.PARKING].filter(Boolean)
    },
    {
      key: "PRISE_RECHARGE",
      label: t("map.filters.priseRecharge", { defaultValue: "Prise recharge" }),
      iconType: SERVICES_TYPES.PRISE_REHARGE,
      serviceTypes: [SERVICES_TYPES.PRISE_REHARGE].filter(Boolean)
    },
    //AYB 26.08.25 affichage traduit du type 8 EAU_ELEC dans le filtre carte
    {
      key: "EAU_ELEC",
      label: t("services_types.eau_elec", { defaultValue: "EAU_ELEC" }),
      iconType: SERVICES_TYPES.EAU_ELEC,
      serviceTypes: [SERVICES_TYPES.EAU_ELEC].filter(Boolean)
    }
  ];

  const getServiceGroupKeyForType = (serviceType) => {
    const group = SERVICE_TYPE_GROUPS.find((g) => g.serviceTypes.includes(serviceType));
    return group ? group.key : null;
  };

  const availableServiceTypes = useMemo(() => {
    if (!markers || markers.length === 0) {
      return [];
    }

    const groupSet = new Set();
    markers.forEach((marker) => {
      (marker.serviceList || []).forEach((serviceType) => {
        const groupKey = getServiceGroupKeyForType(serviceType);
        if (groupKey) groupSet.add(groupKey);
      });
    });

    // Ordre stable pour garder le même rendu
    return SERVICE_TYPE_GROUPS.filter((g) => groupSet.has(g.key)).map((g) => ({
      value: g.key,
      label: g.label,
      iconType: g.iconType
    }));
  }, [markers, t]);

  const filteredMarkers = useMemo(() => {
    if (!markers || markers.length === 0) {
      return [];
    }

    if (!selectedServiceTypes.length) {
      return markers;
    }

    return markers.filter((item) => {
      const itemGroupSet = new Set(
        (item.serviceList || [])
          .map((serviceType) => getServiceGroupKeyForType(serviceType))
          .filter(Boolean)
      );

      // Logique ET: la borne doit contenir tous les groupes sélectionnés
      return selectedServiceTypes.every((selectedGroupKey) => itemGroupSet.has(selectedGroupKey));
    });
  }, [markers, selectedServiceTypes]);

  const toggleServiceType = (serviceType) => {
    setSelectedServiceTypes((prev) =>
      prev.includes(serviceType)
        ? prev.filter((type) => type !== serviceType)
        : [...prev, serviceType]
    );
  };
  // #endregion

  // #region EVENTS
  // Déplacement de la carte vers la borne sélectionnée ou retour vers l'utilisateur
  const handleMoveToBorne = (item) => {
    if (item) {
      setMapCenter({ lat: item.lat, lng: item.lng });
      setActiveMarker(item.value);
    } else {
      setMapCenter({ ...center });
    }
  };
  //#endregion

  // #region HOOK D'EFFET
  // Chargement de la carte
  useEffect(() => {
    setMapCenter(center);
  }, [center]);

  useEffect(() => {
    if (!map || !markers || markers.length === 0 || !window.google) {
      return;
    }

    const legendDiv = document.createElement('div');
    legendDiv.className = 'Map-Legend-Native';
    
    const createMarkerIcon = (iconConfig) => {
      const scale = LEGEND_ICON_SCALE;
      const viewBoxSize = 24;

      const centerOffsetX = (viewBoxSize - (viewBoxSize * scale)) / 2;
      const centerOffsetY = (viewBoxSize - (viewBoxSize * scale)) / 2;
      
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', LEGEND_ICON_SIZE.toString());
      svg.setAttribute('height', LEGEND_ICON_SIZE.toString());
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.style.marginRight = '8px';
      svg.style.flexShrink = '0';
      svg.style.overflow = 'visible'; 
      
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  
      g.setAttribute('transform', `translate(${centerOffsetX}, ${centerOffsetY}) scale(${scale})`);
      
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', iconConfig.path);
      path.setAttribute('fill', iconConfig.fillColor);
      path.setAttribute('stroke', iconConfig.strokeColor || iconConfig.fillColor);
      path.setAttribute('stroke-width', iconConfig.strokeWeight || 0);
      path.setAttribute('fill-opacity', iconConfig.fillOpacity || 1);
      
      g.appendChild(path);
      svg.appendChild(g);
      
      return svg;
    };

    const titleDiv = document.createElement('div');
    titleDiv.className = 'Map-Legend-Native-Title';
    titleDiv.textContent = t("accueil.legendTitle");
    legendDiv.appendChild(titleDiv);

    const items = [
      { icon: ICON_ACTIVE, label: t("accueil.markerActive") },
      { icon: ICON_INACTIVE, label: t("accueil.markerInactive") },
      { icon: ICON_PAIEMENT_SUR_PLACE, label: t("achat.paiementSurPlace") }
    ];

    items.forEach(item => {
      const itemDiv = document.createElement('div');
      itemDiv.className = 'Map-Legend-Native-Item';
      
      const iconSvg = createMarkerIcon(item.icon);
      itemDiv.appendChild(iconSvg);
      
      const span = document.createElement('span');
      span.textContent = item.label;
      itemDiv.appendChild(span);
      
      legendDiv.appendChild(itemDiv);
    });

    map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(legendDiv);

    return () => {
      const controls = map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM];
      const index = controls.getArray().indexOf(legendDiv);
      if (index > -1) {
        controls.removeAt(index);
      }
    };
  }, [map, markers, t]);
  // #endregion

  // #region INTERFACE
  const TooltipInterface = (item) => (
    <InfoWindow onCloseClick={() => setActiveMarker(null)} headerContent={item.name} width={980}>
      <div className="Map-Tooltip-Container" key={item.id}>
        <h3>
          {item.name}
        </h3>
        <div className="Map-Tooltip-Description">
          <span>{t("accueil.serviceTooltip")}</span>
          <div className="Map-Tooltip-Services">
            {item.serviceList
              .filter((serviceType) => !(serviceType === 9 && item.serviceList.includes(4)))
              .map((serviceType, index) => (
                <Icon className="Map-Tooltip-Icon" key={index} icon={selectIcon(serviceType.toString())} height={32} width={32} />
              ))}
          </div>
        </div>
        {item.active && (item.idModePaiement !== 1 && item.idModePaiement !== 5)
          ?
          <Fab
            label={t("accueil.mapButton")}
            icon="shopping_cart"
            onClick={() => onClick(item)}
            style={{ boxShadow: "-1px 2px 3px -1px rgba(0, 0, 0, 0.5), 1px 4px 0px 0px rgba(0, 0, 0, 0.14), 0px 1px 0px 0px rgba(0, 0, 0, 0.12)" }}
          />
          : (item.idModePaiement === 1 || item.idModePaiement === 5)
          ?
          <Fab
            label={t("achat.paiementSurPlaceComplet")}
            icon="close"
            style={{ boxShadow: "-0px 0px 0px -0px rgba(0, 0, 0, 0.5), 0px 0px 0px 0px rgba(0, 0, 0, 0.14), 0px 0px 0px 0px rgba(0, 0, 0, 0.12)", backgroundColor: "black" }}
          />
          :
          <Fab
            label={t("accueil.mapButtonNotAvailable")}
            icon="close"
            style={{ boxShadow: "-0px 0px 0px -0px rgba(0, 0, 0, 0.5), 0px 0px 0px 0px rgba(0, 0, 0, 0.14), 0px 0px 0px 0px rgba(0, 0, 0, 0.12)", backgroundColor: "black" }}
          />
        }
      </div>
    </InfoWindow>
  );

  // Interface de la carte
  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    rotateControl: false,
    scaleControl: false
  };

  return (
    isLoaded ?
      <GoogleMap
        {...props}
        onLoad={(mapInstance) => setMap(mapInstance)}
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={zoom}
        options={mapOptions}
      >
        <div className="Map-TopControls">
          {/* Sélecteur de borne */}
          <div className="Map-SelectBorne">
            <Select
              isClearable
              onChange={(value) => handleMoveToBorne(value)}
              options={terminauxSelector}
              placeholder={t("accueil.searchBorne")}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  width: "100%"
                }),
                container: (baseStyles) => ({
                  ...baseStyles,
                  width: "100%"
                }),
              }}
            />
          </div>
          {/* Option 3/4: toggle texte (decommente pour tester)
          <div className="Map-ServiceFiltersToggle-Wrapper">
            <button
              type="button"
              className="Map-ServiceFiltersToggle"
              onClick={() => setShowServiceFilters((prev) => !prev)}
            >
              <span className="Map-ServiceFiltersToggle-Icon" aria-hidden="true">⚙</span>
              <span>{showServiceFilters ? "Masquer le filtre" : "Filtrer par service"}</span>
              {!!selectedServiceTypes.length && (
                <span className="Map-ServiceFiltersToggle-Count">{selectedServiceTypes.length}</span>
              )}
            </button>
          </div>
          */}

          <div className="Map-ServiceFiltersToggle-Wrapper Map-ServiceFiltersToggle-Wrapper--compact">
            <button
              type="button"
              className="Map-ServiceFiltersToggle Map-ServiceFiltersToggle--compact"
              onClick={() => setShowServiceFilters((prev) => !prev)}
              aria-label={showServiceFilters
                ? t("map.filters.hideServices", { defaultValue: "Masquer les services" })
                : t("map.filters.showServices", { defaultValue: "Filtrer les services" })}
            >
              <Icon icon="tune" />
              {!!selectedServiceTypes.length && (
                <span className="Map-ServiceFiltersToggle-Count Map-ServiceFiltersToggle-Count--dot" aria-label={`${selectedServiceTypes.length} filtre(s) actif(s)`} />
              )}
            </button>
          </div>
        </div>

        {/* Option 3/4: react-select compact moderne (decommente pour tester)
        {showServiceFilters && (
          <div className="Map-ServiceFilters Map-ServiceFilters--select">
            <Select
              isMulti
              isClearable
              options={availableServiceTypes}
              value={availableServiceTypes.filter((option) => selectedServiceTypes.includes(option.value))}
              placeholder="Filtrer par type de service"
              onChange={(values) => setSelectedServiceTypes((values || []).map((value) => value.value))}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  minWidth: "min(95%, 420px)",
                  borderRadius: "12px",
                  borderColor: "#d4dce8",
                  boxShadow: "0 10px 25px rgba(13, 36, 69, 0.1)"
                })
              }}
            />
          </div>
        )}
        */}

        {/* Option 1/3: chips modernes (decommente pour tester)
        <div className="Map-ServiceFilters">
          <div className="Map-ServiceFilters-Header">
            <span className="Map-ServiceFilters-Title">Services</span>
            <button
              type="button"
              className="Map-ServiceFilters-Reset"
              onClick={() => setSelectedServiceTypes([])}
              disabled={selectedServiceTypes.length === 0}
            >
              Reinitialiser
            </button>
          </div>
          <div className="Map-ServiceFilters-Chips">
            <button
              type="button"
              className={`Map-ServiceFilterButton ${selectedServiceTypes.length === 0 ? "is-active" : ""}`}
              onClick={() => setSelectedServiceTypes([])}
            >
              Tous
            </button>
            {availableServiceTypes.map((serviceOption) => (
              <button
                key={serviceOption.value}
                type="button"
                className={`Map-ServiceFilterButton ${selectedServiceTypes.includes(serviceOption.value) ? "is-active" : ""}`}
                onClick={() => toggleServiceType(serviceOption.value)}
              >
                {serviceOption.label}
              </button>
            ))}
          </div>
        </div>
        */}

        {/* Option 2/3: liste checklist moderne (decommente pour tester)
        <div className="Map-ServiceFilters Map-ServiceFilters--checkboxes">
          <div className="Map-ServiceFilters-Header">
            <span className="Map-ServiceFilters-Title">Services</span>
          </div>
          <label className="Map-ServiceFilterCheckbox">
            <input
              type="checkbox"
              checked={selectedServiceTypes.length === 0}
              onChange={() => setSelectedServiceTypes([])}
            />
            Tous
          </label>
          {availableServiceTypes.map((serviceOption) => (
            <label className="Map-ServiceFilterCheckbox" key={serviceOption.value}>
              <input
                type="checkbox"
                checked={selectedServiceTypes.includes(serviceOption.value)}
                onChange={() => toggleServiceType(serviceOption.value)}
              />
              {serviceOption.label}
            </label>
          ))}
        </div>
        */}

        {/* Filtres services - Option active (4/4): panneau compact style mobile */}
        {showServiceFilters && (
          <div className="Map-ServiceFilters Map-ServiceFilters--compact">
            <div className="Map-ServiceFilters-Header">
              <span className="Map-ServiceFilters-Title">{t("map.filters.title", { defaultValue: "Services" })}</span>
              <div className="Map-ServiceFilters-HeaderActions">
                <button
                  type="button"
                  className="Map-ServiceFilters-Hide"
                  onClick={() => setShowServiceFilters(false)}
                >
                  {t("map.filters.hide", { defaultValue: "Cacher" })}
                </button>
                <button
                  type="button"
                  className="Map-ServiceFilters-Reset"
                  onClick={() => setSelectedServiceTypes([])}
                  disabled={selectedServiceTypes.length === 0}
                >
                  {t("map.filters.reset", { defaultValue: "Reinitialiser" })}
                </button>
              </div>
            </div>
            <div className="Map-ServiceFilters-Chips">
              <button
                type="button"
                className={`Map-ServiceFilterButton ${selectedServiceTypes.length === 0 ? "is-active" : ""}`}
                onClick={() => setSelectedServiceTypes([])}
              >
                {t("map.filters.all", { defaultValue: "Tous" })}
              </button>
              {availableServiceTypes.map((serviceOption) => (
                <button
                  key={serviceOption.value}
                  type="button"
                  className={`Map-ServiceFilterButton ${selectedServiceTypes.includes(serviceOption.value) ? "is-active" : ""}`}
                  onClick={() => toggleServiceType(serviceOption.value)}
                >
                  {!!serviceOption.iconType && (
                    <Icon
                      className="Map-ServiceFilterButton-Icon"
                      icon={selectIcon(serviceOption.iconType.toString())}
                    />
                  )}
                  {serviceOption.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Marqueurs */}
        {isMarkerShown &&
          <Marker position={center ? center : defaultCenter} />
        }
        {filteredMarkers &&
          filteredMarkers.map((item) => {
            return (
              <MarkerF
                key={item.id}
                position={item.location}
                icon={{
                  ...item.icon,
                  anchor: new window.google.maps.Point(15, 20)
                }}
                onClick={() => { setActiveMarker(item.id) }}
              >
                {
                  activeMarker === item.id
                    ?
                    // Interface du tooltip d'un marker
                    TooltipInterface(item)
                    :
                    null
                }
              </MarkerF>
            )
          }
          )}
      </GoogleMap>
      :
      <></>
  )
  // #endregion
}

export { MapComponent };

